import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import api from "../bootstrapApi";

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export default function ShopStatusPage() {
  const accent = "#e86026";
  const [form, setForm] = useState({ reference: "", whatsappNumber: "" });
  const [message, setMessage] = useState(null);
  const [result, setResult] = useState(null);

  const handleLookup = async () => {
    try {
      const response = await api.get("/api/public/status-lookup", {
        params: {
          reference: form.reference,
          whatsappNumber: form.whatsappNumber,
        },
      });
      setResult(response.data);
      setMessage(null);
    } catch (_error) {
      setResult(null);
      setMessage({
        type: "error",
        text: "Aucun dossier n'a ete trouve pour cette reference et ce numero WhatsApp.",
      });
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <Box
        sx={{
          borderRadius: "34px",
          p: { xs: 3, md: 4 },
          background:
            "radial-gradient(circle at top right, rgba(232,96,38,0.16), transparent 30%), linear-gradient(180deg, rgba(255,248,243,0.98) 0%, rgba(247,239,234,0.98) 100%)",
          border: "1px solid #f0ddd3",
          boxShadow: "0 24px 48px rgba(101,110,143,0.10)",
        }}
      >
        <Typography variant="overline" sx={{ color: "#72809a", letterSpacing: "0.14em" }}>
          SUIVI
        </Typography>
        <Typography variant="h3" sx={{ mt: 1, mb: 1.25, color: "#3b302f" }}>
          Suivre votre commande ou votre demande
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
          Entrez votre reference et le meme numero WhatsApp utilise lors de la demande pour voir l'etat actuel.
        </Typography>
      </Box>

      <Box
        sx={{
          borderRadius: "30px",
          p: 3,
          background: "#fff",
          border: "1px solid #f0ddd3",
          boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
          display: "grid",
          gap: 2,
        }}
      >
        {message ? <Alert severity={message.type}>{message.text}</Alert> : null}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.4}>
          <TextField
            label="Reference"
            value={form.reference}
            onChange={(event) => setForm((current) => ({ ...current, reference: event.target.value.toUpperCase() }))}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Numero WhatsApp"
            value={form.whatsappNumber}
            onChange={(event) => setForm((current) => ({ ...current, whatsappNumber: event.target.value }))}
            sx={{ flex: 1 }}
          />
          <Button variant="contained" onClick={handleLookup} disabled={!form.reference || !form.whatsappNumber}>
            Charger le statut
          </Button>
        </Stack>
      </Box>

      {result ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.9fr" }, gap: 3 }}>
          <Box
            sx={{
              borderRadius: "30px",
              p: 3,
              background: "#fff",
              border: "1px solid #f0ddd3",
              boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
              display: "grid",
              gap: 2,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
              <Box>
                <Typography variant="h5" sx={{ color: "#3b302f" }}>
                  {result.contactName || "Dossier client"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reference {result.reference} | {result.type === "order" ? "Commande" : "Prospect"}
                </Typography>
              </Box>
              <Chip label={result.currentStatus} sx={{ borderRadius: "999px", fontWeight: 700, bgcolor: accent, color: "#fff" }} />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} flexWrap="wrap">
              {result.leadReference ? <Chip label={`Prospect ${result.leadReference}`} sx={{ borderRadius: "999px" }} /> : null}
              {result.orderReference ? <Chip label={`Commande ${result.orderReference}`} sx={{ borderRadius: "999px" }} /> : null}
              <Chip label={result.locationText || "Localisation a confirmer"} sx={{ borderRadius: "999px" }} />
            </Stack>

            <Divider />

            <Box sx={{ display: "grid", gap: 1.4 }}>
              <Typography variant="h6" sx={{ color: "#3b302f" }}>
                Articles
              </Typography>
              {result.items.map((item, index) => (
                <Box
                  key={`${item.productName}-${index}`}
                  sx={{
                    p: 2,
                    borderRadius: "22px",
                    border: "1px solid #f0ddd3",
                    background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(247,249,252,0.96) 100%)",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#3b302f" }}>{item.productName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.categoryName || "Categorie"} | Quantite {item.quantity} | {Number(item.unitPrice || 0).toFixed(2)} EUR
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "grid", gap: 3 }}>
            <Box
              sx={{
                borderRadius: "30px",
                p: 3,
                background: "#fff",
                border: "1px solid #f0ddd3",
                boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
                display: "grid",
                gap: 1.5,
              }}
            >
              <Typography variant="h6" sx={{ color: "#3b302f" }}>
                Etat du rendez-vous
              </Typography>
              {result.appointment ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Statut: {result.appointment.status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Technicien: {result.appointment.technicianName || "Pas encore affecte"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Option A: {formatDateTime(result.appointment.optionAStart)} - {formatDateTime(result.appointment.optionAEnd)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Option B: {formatDateTime(result.appointment.optionBStart)} - {formatDateTime(result.appointment.optionBEnd)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Confirme: {formatDateTime(result.appointment.confirmedStart)} - {formatDateTime(result.appointment.confirmedEnd)}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucun creneau n'a encore ete propose pour ce dossier.
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                borderRadius: "30px",
                p: 3,
                background: "#fff",
                border: "1px solid #f0ddd3",
                boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
                display: "grid",
                gap: 1.4,
              }}
            >
              <Typography variant="h6" sx={{ color: "#3b302f" }}>
                Historique
              </Typography>
              {result.history.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{
                    p: 1.6,
                    borderRadius: "22px",
                    border: "1px solid #f0ddd3",
                    background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(247,249,252,0.96) 100%)",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#3b302f" }}>{entry.newStatus}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {entry.note || "Statut mis a jour"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(entry.createdAt)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
